import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Column, Row, Page, Loader, Spinner, Drawer } from '@oliasoft-open-source/react-ui-library';
import { CostElementsList } from 'views/cost-setup/cost-elements.list';
import { CostCategoryForm } from 'views/cost-setup/cost-category.form';
import { NoItems } from 'views/no-items/no-items';
import {
  addInitialCostItem,
  getElements,
  showCreateCostSetupModelModalUpdated,
  createCostSetupModel,
  clearCreateModelFlags,
  costCleanup,
  showImportFromPriceBookModalUpdated,
  importBulkCostItems,
  initialCostItem,
} from 'store/entities/cost-setup/cost-setup';
import { clearPriceBook, getPriceBookCompanyItems } from 'src/store/entities/price-book/price-book';
import { CostItemForm } from 'views/cost-setup/cost-item.form';
import { CostItemDeleteModal } from 'views/cost-setup/cost-item-delete.modal';
import { CostCategoryDeleteModal } from 'views/cost-setup/cost-category-delete.modal';
import { CostCategoryRenameModal } from 'views/cost-setup/cost-category-rename.modal';
import translations from 'src/internationalisation/translation-map.json';
import { CreateModelModal } from 'views/create-model/create-model.modal';
import { getOperations, getSections, operationsCleared } from 'store/entities/activity-model/activity-model';
import { getWellDetails } from 'store/entities/well-details/well-details';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { getCostCodesItems } from 'src/store/entities/cost-codes/cost-codes';
import { getCompanyCostSetup } from 'src/store/entities/company-settings/company-settings';
import { typeQuantityMap } from 'src/enums/price-book';
import { selectPrimaryCurrency } from 'store/entities/company-settings/selectors';
import { CreateModelErrorsModal } from '../create-model/create-model-errors.modal';
import { CostSetupDrawer } from './drawer/cost-setup.drawer';
import { ImportFromPriceBookModal } from './cost-import-from-pricebook/cost-import-from-price-book.modal';

const CostSetup = ({
  list,
  isFetching,
  isAdding,
  getElements,
  activeCategory,
  activeCostItem,
  addInitialCostItem,
  isPageDisabled,
  showCreateCostSetupModelModalUpdated,
  showCreateCostSetupModelModal,
  createCostSetupModel,
  isSchematicEmpty,
  isTemplatesEmpty,
  clearCreateModelFlags,
  costCleanup,
  getOperations,
  getSections,
  getWellDetails,
  showImportFromPriceBookModalVisible,
  showImportFromPriceBookModalUpdated,
  getCostCodesItems,
  importBulkCostItems,
  primaryCurrency,
  clearPriceBook,
  getPriceBookCompanyItems,
  getCompanyCostSetup,
  isWellDetailsFetching,
}) => {
  const { t } = useTranslation();
  const { project, company } = useParams();
  const [activeForm, setActiveForm] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [costItemModalVisible, setCostItemModalVisible] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryRenameModalVisible, setCategoryRenameModalVisible] = useState(false);

  const [importWithLink, setImportWithLink] = useState(false);

  useEffect(() => {
    getElements(project);
    getOperations(project);
    getWellDetails(project);
    getSections(project);
    getCostCodesItems();
    getPriceBookCompanyItems();
    getCompanyCostSetup(company);

    return () => {
      operationsCleared();
      costCleanup();
      clearPriceBook();
    };
  }, [project]);

  useEffect(() => {
    if (activeCategory) setActiveForm('category');
    else if (activeCostItem) setActiveForm('item');
  }, [activeCategory, activeCostItem]);

  const onClickAddItem = (parentId = null) => {
    addInitialCostItem(project, t(translations.newItem), primaryCurrency, parentId);
    setActiveForm('item');
  };

  const forms = {
    category: <CostCategoryForm projectId={project} category={activeCategory} isPageDisabled={isPageDisabled} />,
    item: (
      <CostItemForm
        projectId={project}
        costItem={activeCostItem}
        isPageDisabled={isPageDisabled}
        primaryCurrency={primaryCurrency}
      />
    ),
  };
  let form = null;

  if (list.length === 0 && !isFetching) {
    form = (
      <NoItems
        onClickAdd={onClickAddItem}
        showCreateModelModalUpdated={showCreateCostSetupModelModalUpdated}
        isPageDisabled={isPageDisabled}
        type="costSetup"
      />
    );
  }
  if (activeForm) {
    form = forms[activeForm];
  }
  const handleSubmit = (parentId, priceBookList, isLinked) => {
    const costItems = priceBookList.map((priceBookItem) => {
      let newObject = {
        ...initialCostItem,
        parentId,
        name: priceBookItem.name,
        cost: priceBookItem.price,
        currency: priceBookItem.currency,
        vendor: priceBookItem.vendor,
        costCodesId: priceBookItem.costCodesId,
        type: priceBookItem.priceType,
        isLinked,
        priceBookItemId: priceBookItem.priceBookItemId,
      };

      const typeQuantity = typeQuantityMap[priceBookItem.priceType];

      newObject = {
        ...newObject,
        type: typeQuantity.type,
        quantity: typeQuantity.quantity,
      };

      // Set unitCost if the type is 'quantity'
      if (typeQuantity.type === 'quantity') {
        newObject.unitCost = priceBookItem.price;
      }

      return newObject;
    });
    importBulkCostItems({ costItems, projectId: project });
  };
  const [drawerWidth, setDrawerWidth] = useState(400);

  return (
    <>
      <Page padding={false}>
        {(isFetching || isWellDetailsFetching) && (
          <Loader text="Fetching..." theme="light" cover>
            <Spinner dark />
          </Loader>
        )}
        <Row spacing={0} height="100%">
          <CostSetupDrawer />
          <Drawer
            width={drawerWidth}
            closedWidth={50}
            open
            border
            onResize={(width) => setDrawerWidth(width)}
            background="none"
          >
            <Column scroll width="400px" borderRight testId="cost-setup-left-bar" spacing={0}>
              <CostElementsList
                setActiveForm={setActiveForm}
                elements={list}
                projectId={project}
                setSelectedId={setSelectedId}
                setCostItemModalVisible={setCostItemModalVisible}
                setCategoryModalVisible={setCategoryModalVisible}
                onClickAddItem={onClickAddItem}
                isPageDisabled={isPageDisabled}
                setCategoryRenameModalVisible={setCategoryRenameModalVisible}
                isAdding={isAdding}
                showImportFromPriceBookModalUpdated={showImportFromPriceBookModalUpdated}
                setImportWithLink={setImportWithLink}
              />
            </Column>
          </Drawer>
          <Column scroll padding>
            {form}
          </Column>
        </Row>
      </Page>
      {costItemModalVisible && (
        <CostItemDeleteModal
          setModalVisible={setCostItemModalVisible}
          costItemId={selectedId}
          setActiveForm={setActiveForm}
          projectId={project}
        />
      )}
      {categoryModalVisible && (
        <CostCategoryDeleteModal
          setModalVisible={setCategoryModalVisible}
          costCategoryId={selectedId}
          setActiveForm={setActiveForm}
          projectId={project}
        />
      )}
      {categoryRenameModalVisible && (
        <CostCategoryRenameModal
          setModalVisible={setCategoryRenameModalVisible}
          costCategoryId={selectedId}
          category={activeCategory}
          projectId={project}
        />
      )}
      {showCreateCostSetupModelModal && (
        <CreateModelModal
          projectId={project}
          showCreateModelModalUpdated={showCreateCostSetupModelModalUpdated}
          createModel={createCostSetupModel}
          isFetching={isFetching}
        />
      )}
      {(isSchematicEmpty || isTemplatesEmpty) && (
        <CreateModelErrorsModal
          isSchematicEmpty={isSchematicEmpty}
          isTemplatesEmpty={isTemplatesEmpty}
          company={company}
          project={project}
          clearCreateModelFlags={clearCreateModelFlags}
        />
      )}
      {showImportFromPriceBookModalVisible && (
        <ImportFromPriceBookModal
          parentId={selectedId}
          showImportFromPriceBookModalUpdated={showImportFromPriceBookModalUpdated}
          handleSubmit={handleSubmit}
          importWithLink={importWithLink}
        />
      )}
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const {
    isAdding,
    isFetching,
    list,
    showCreateCostSetupModelModal,
    isSchematicEmpty,
    isTemplatesEmpty,
    showImportFromPriceBookModalVisible,
  } = entities.costSetup;
  const activeCategory = list.find((item) => item.costCategoryId && item.active);
  const activeCostItem = list.find((item) => item.costItemId && item.active);
  const isWellDetailsFetching = entities.wellDetails.isFetching;

  return {
    isFetching,
    isAdding,
    list,
    activeCategory,
    activeCostItem,
    showCreateCostSetupModelModal,
    isSchematicEmpty,
    isTemplatesEmpty,
    showImportFromPriceBookModalVisible,
    primaryCurrency: selectPrimaryCurrency(entities),
    isWellDetailsFetching,
  };
};

const mapDispatchToProps = {
  getElements,
  addInitialCostItem,
  showCreateCostSetupModelModalUpdated,
  createCostSetupModel,
  clearCreateModelFlags,
  costCleanup,
  getOperations,
  getSections,
  getWellDetails,
  showImportFromPriceBookModalUpdated,
  getCostCodesItems,
  importBulkCostItems,
  clearPriceBook,
  getPriceBookCompanyItems,
  getCompanyCostSetup,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(CostSetup), { isPage: true });

export { Container as CostSetup };
